import * as React from 'react'
import {ContentAreas} from '@freckle/student-entities/ts/common/helpers/content-area'
import type {SelfAssignedTargetedPracticeTypeT} from '@freckle/student-entities/ts/math/common/models/targeted-practice-type/self-assigned-targeted-practice-type'
import type {LangT} from '@freckle/student-materials/src/helpers/languages'
import {LoggedRoute} from '@freckle/student/ts/common/routers/logged-route'
import {type Chrome} from '@freckle/student/ts/pages/chromeless-launch/components/use-chrome'
import {NavigateToTeacherAssignedMathTargeted} from './legacy-route-redirects'
import {
  NumberFactsProgressHandler,
  NumberBasicsQuestionDebuggerHandler,
  MathPracticeHintDebugHandler,
  NumberBasicsBriefingPageHandler,
  NumberBasicsSessionHandler,
  MathMenuHandler,
  MathAssessmentQuestionSessionHandler,
  MathIblVideoHandler,
  MathTargetedHandler,
  MathAdaptiveSessionOrDiagnosticHandler,
  MathDiagnosticHandler,
  MathAdaptiveTeacherAssignedSessionHandler,
  MathAdaptiveDebugHandler,
  MathTargetedSelfAssignedHandler,
  FactPracticeSelfAssignedSessionHandler,
  FactPracticeTeacherAssignedSessionHandler,
  NumberFactsSelfAssignedHandler,
  NumberFactsTeacherAssignedHandler,
  MathDomainSelectionHandler,
  MathQuestionEditor,
  DomainBriefingHandler,
  MathDepthOfKnowledgeSessionHandler,
  MathTargetedDepthOfKnowledgeSessionHandler,
  FocusSkillsPracticeHandler,
  HighSchoolCoursesHandler,
  CourseBriefingHandler,
  DebugMathjaxHandler,
  DebugWorkedExampleHandler,
} from './math-lazy'
import type {PathData} from './path-data'

export type MathRoutesWithQueryParams = {
  '/math/menu': undefined
  '/math/number-facts': undefined
  '/math/number-facts/progress': undefined
  '/math/number-facts/assignments/:assignmentIds/session': undefined
  '/math/number-basics': undefined
  '/math/number-basics/session': undefined
  '/math/number-basics/questions/:questionUuid': undefined
  '/assignments/assessment/:assignmentIds/session': undefined
  '/assignments/ibl/:assignmentIds/session': undefined
  // Self assigned Math Targeted Assignment creation route
  '/questions/:domainId/:standardId': SelfAssignedTargetedPracticeTypeT
  '/questions/skill/:skillId': {
    chrome: Chrome
    returnTo: string
    numQuestions: number
  }
  '/math/targeted/self-assigned/assignments/:assignmentIds/session':
    | (SelfAssignedTargetedPracticeTypeT & {
        alwaysCollectSentiment?: boolean
      })
    | {alwaysCollectSentiment?: boolean}
    | null
  '/math/targeted/teacher-assigned/self-retry/assignments/:assignmentIds/session': {
    tag: 'retry'
    originalAssignmentId: number
  }
  '/math/targeted/teacher-assigned/assignments/:assignmentIds/session':
    | {isFocusSkillsPractice: boolean}
    | {
        chrome: Chrome
        returnTo: string
      }
    | null
    | undefined
  // Legacy Math Targeted START
  '/assignments/standard/:assignmentIds/session': undefined
  '/assignments/targeted/:assignmentIds/session':
    | {isFocusSkillsPractice: boolean}
    | null
    | undefined
  // Legacy Math Targeted END

  '/math/diagnostic/:rlDomainId': {complete: 'true' | 'false'; forAssignmentRoute?: string}
  '/questions/:rlDomainId': {personalizedPathway?: 'true' | 'false'}
  '/assignments/adaptive/:assignmentIds/session': undefined
  '/debugquestions/:questionId?': undefined
  '/fact-practice': undefined
  '/assignments/fact-practice/:assignmentIds/session': undefined
  '/domains': undefined
  '/math/editor': undefined
  '/briefing/:rlDomainId/adaptive': undefined
  '/math/depth-of-knowledge/:standardId': undefined
  '/math/depth-of-knowledge/targeted/assignments/:assignmentIds': undefined
  '/math/focus-skills-remediation': undefined
  '/math/practice/hint/debug': undefined
  '/math/high-school-courses': undefined
  '/math/course/:skillSetId': undefined
  '/debuggers/mathjax': undefined
  '/debuggers/worked-example/:stemId': {lang?: LangT}
}

export const mathPathData: PathData<MathRoutesWithQueryParams> = {
  '/math/menu': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute component={MathMenuHandler} wrapperParams={{isResponsive: true}} path={path} />
    ),
  },
  '/math/number-facts': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={NumberFactsSelfAssignedHandler} path={path} />,
  },
  '/math/number-facts/progress': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={NumberFactsProgressHandler} path={path} />,
  },
  '/math/number-facts/assignments/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={NumberFactsTeacherAssignedHandler} path={path} />,
  },
  '/math/number-basics': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={NumberBasicsBriefingPageHandler} path={path} />,
  },
  '/math/number-basics/session': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={NumberBasicsSessionHandler} path={path} />,
  },
  '/math/number-basics/questions/:questionUuid': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={NumberBasicsQuestionDebuggerHandler} path={path} />,
  },
  '/assignments/assessment/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathAssessmentQuestionSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/assignments/ibl/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={MathIblVideoHandler} path={path} />,
  },

  // Legacy Math Targeted Routes START
  '/assignments/standard/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: () => <NavigateToTeacherAssignedMathTargeted />,
  },
  '/assignments/targeted/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: () => <NavigateToTeacherAssignedMathTargeted />,
  },
  // Legacy Math Targeted Routes END

  '/math/targeted/teacher-assigned/assignments/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathTargetedHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },

  '/math/diagnostic/:rlDomainId': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={MathDiagnosticHandler} path={path} />,
  },
  // Self assigned Math Targeted Assignment creation route
  '/questions/:rlDomainId': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={MathAdaptiveSessionOrDiagnosticHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },

  '/math/targeted/self-assigned/assignments/:assignmentIds/session': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={MathTargetedHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },

  '/math/targeted/teacher-assigned/self-retry/assignments/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathTargetedHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },

  '/assignments/adaptive/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathAdaptiveTeacherAssignedSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/debugquestions/:questionId?': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={MathAdaptiveDebugHandler} path={path} />,
  },
  '/questions/:domainId/:standardId': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathTargetedSelfAssignedHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/questions/skill/:skillId': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathTargetedSelfAssignedHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/fact-practice': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={FactPracticeSelfAssignedSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/assignments/fact-practice/:assignmentIds/session': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={FactPracticeTeacherAssignedSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/domains': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={MathDomainSelectionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/math/editor': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={MathQuestionEditor} path={path} />,
  },
  '/briefing/:rlDomainId/adaptive': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={DomainBriefingHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/math/depth-of-knowledge/:standardId': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathDepthOfKnowledgeSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/math/depth-of-knowledge/targeted/assignments/:assignmentIds': {
    isBlockedByFocusMode: false,
    element: path => (
      <LoggedRoute
        component={MathTargetedDepthOfKnowledgeSessionHandler}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/math/focus-skills-remediation': {
    isBlockedByFocusMode: true,
    element: path => (
      <LoggedRoute
        component={() => <FocusSkillsPracticeHandler contentArea={ContentAreas.math} />}
        wrapperParams={{isResponsive: true}}
        path={path}
      />
    ),
  },
  '/math/practice/hint/debug': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={MathPracticeHintDebugHandler} path={path} />,
  },
  '/math/high-school-courses': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={HighSchoolCoursesHandler} path={path} />,
  },
  '/math/course/:skillSetId': {
    isBlockedByFocusMode: true,
    element: path => <LoggedRoute component={CourseBriefingHandler} path={path} />,
  },
  '/debuggers/mathjax': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={DebugMathjaxHandler} path={path} />,
  },
  '/debuggers/worked-example/:stemId': {
    isBlockedByFocusMode: false,
    element: path => <LoggedRoute component={DebugWorkedExampleHandler} path={path} />,
  },
}
